import { IS_E2E } from '@omnipresentgroup/constants';
import {
  DropdownButton,
  getISOString,
  notifyError,
  notifySuccess,
} from '@omnipresentgroup/design-system';
import { useExportExpenses } from '@omnipresentgroup/domains';
import { type BaseExpense, FileFormat } from '@omnipresentgroup/types';
import { useState } from 'react';

const EXPORT_EXPENSES_ERROR_TITLE = 'Error downloading expenses';

const EXPORT_EXPENSES_WITH_ATTACHMENTS_TITLE =
  'Preparing your file for download';
const EXPORT_EXPENSES_WITH_ATTACHMENTS_DESCRIPTION =
  'We are preparing your file for download. We will be sending a link to your email to download the zipped file. Thank you for your patience.';

export const DownloadExpensesAction = ({
  companyId,
  expenses,
  iconOnly = false,
  onSuccess,
}: {
  companyId: string;
  expenses: BaseExpense[];
  iconOnly?: boolean;
  onSuccess?: () => void;
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { downloadExpenses } = useExportExpenses();

  const handleExportExpenses = ({
    includeAttachments = false,
  }: {
    includeAttachments?: boolean;
  }) => {
    setIsDownloading(true);
    downloadExpenses({
      exportFormat: FileFormat.CSV,
      ...(includeAttachments && {
        includeAttachments: true,
      }),
      approverId: companyId,
      expenseIds: expenses.map((expense) => expense.id),
    })
      .then((content) => {
        if (includeAttachments) {
          notifySuccess({
            title: EXPORT_EXPENSES_WITH_ATTACHMENTS_TITLE,
            description: EXPORT_EXPENSES_WITH_ATTACHMENTS_DESCRIPTION,
          });
        } else {
          // Create a link and click it to download the file (skip for e2e tests)
          if (!IS_E2E) {
            const url = window.URL.createObjectURL(
              new Blob([content], {
                type: 'text/plain',
              }),
            );
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
              'download',
              `bulk-expenses-${getISOString()}.csv`,
            );
            document.body.append(link);
            link.click();
          }
        }
        onSuccess?.();
      })
      .catch((error) => {
        notifyError({
          title: EXPORT_EXPENSES_ERROR_TITLE,
          description: error.message,
        });
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  return (
    <DropdownButton
      variant="secondary"
      size="medium"
      icon={isDownloading ? undefined : 'Download'}
      loading={isDownloading}
      disabled={expenses.length === 0}
      options={[
        {
          name: `Download ${FileFormat.CSV}`,
          onClick: () => handleExportExpenses({}),
          icon: 'Download',
        },
        {
          name: 'Download attachments',
          onClick: () => handleExportExpenses({ includeAttachments: true }),
          icon: 'FileText',
        },
      ]}
    >
      {iconOnly ? '' : `Download ${FileFormat.CSV}`}
    </DropdownButton>
  );
};
